import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Sparkles, ShieldCheck, Loader2 } from "lucide-react";
import BrandLogo from "@/components/BrandLogo";

/**
 * Public /demo — no login required.
 *
 * Provisions the shared read-only demo tenant via /api/demo-workspace/login
 * (issues a normal auth cookie pointing at the isolated `demo-workspace`
 * tenant + demo user), fires a `demo_opened` analytics beacon, then
 * bounces the visitor into `/dashboard` where the DemoWorkspaceBanner
 * takes over.
 *
 * Distinct from Global Demo Workspace:
 *   - Public Demo → separate `demo-workspace` tenant with demo user login
 *   - Global Demo → shared `global-demo` tenant, entered by an
 *                    authenticated user via /api/workspace/switch
 */
export default function DemoAutoLogin() {
    const navigate = useNavigate();
    const [error, setError] = useState(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                // Fire-and-forget analytics beacon — safe even if it fails
                api.post("/demo/events", { event: "demo_opened", source: "public" })
                    .catch(() => {});
                await api.post("/demo-workspace/login");
                if (!cancelled) {
                    // Hard reload so AuthContext bootstraps with the new
                    // demo cookie — SPA navigation won't re-fetch /me
                    // and ProtectedRoute would bounce us back to /login.
                    setTimeout(() => { window.location.href = "/dashboard"; }, 200);
                }
            } catch (e) {
                if (!cancelled) {
                    setError("We couldn't spin up the demo just now. Please try again in a moment.");
                }
            }
        })();
        return () => { cancelled = true; };
    }, [navigate]);

    return (
        <div className="min-h-screen bg-gradient-to-br from-stone-950 via-stone-900 to-amber-950 flex flex-col items-center justify-center px-6 text-stone-100">
            <BrandLogo variant="light" height={40} />
            <div className="mt-10 flex items-center gap-3 text-amber-300">
                <Sparkles size={18} />
                <span className="uppercase tracking-[0.25em] text-[11px] font-semibold">Preparing your demo</span>
            </div>
            <h1 className="mt-6 font-display text-3xl sm:text-4xl text-white text-center">
                Welcome to VAULT
            </h1>
            <p className="mt-3 text-stone-300 text-sm max-w-md text-center">
                Setting up a sandbox with sample financial data. No signup, no card, and nothing you do
                here is saved.
            </p>
            <div className="mt-8">
                {error ? (
                    <div data-testid="demo-error" className="text-rose-300 text-sm">
                        {error}
                    </div>
                ) : (
                    <Loader2 size={22} className="animate-spin text-amber-300" />
                )}
            </div>
            <div className="mt-10 text-[11px] text-stone-500 flex items-center gap-2">
                <ShieldCheck size={12} /> Public demo · Sample data only · Read-only session
            </div>
        </div>
    );
}
